import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  useTheme,
  CircularProgress,
  Autocomplete,
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import EmailIcon from '@mui/icons-material/Email'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import DiscountCodeInput from '@/components/DiscountCodeInput'
import { OrgAccountDetails } from '@/api/orgsAPI'
import { DiscountCodeValidation } from '@/api/subscriptionsAPI'
import {
  calculateGst,
  getBillingAmount,
  TIER_PRICING,
} from '@/utils/billingConstants'
import { useAuth0 } from '@/utils/auth/auth0'

interface BillingInfo {
  billingLegalName: string
  billingEmail: string
  billingContactName: string
  billingContactPhone: string
  billingAddressLine1: string
  billingAddressLine2: string
  billingCity: string
  billingState: string
  billingPostalCode: string
  billingCountry: string
  taxRegistrationNumber: string
  discountCode?: string
}

type FrequencyMultiplier = 1 | 3 | 12

interface InvoicePaymentFormProps {
  tier?: number
  billingFrequency?: 'monthly' | 'quarterly' | 'annual'
  onSubmit: (billingInfo: BillingInfo) => Promise<void>
  isLoading?: boolean
  error?: string
  onBack?: () => void
  orgName?: string
  userEmail?: string
  userFirstName?: string
  userLastName?: string
  existingAccountDetails?: OrgAccountDetails | null
}

export default function InvoicePaymentForm({
  tier = 4,
  billingFrequency: billingFrequencyProp,
  onSubmit,
  isLoading = false,
  error,
  onBack,
  orgName,
  userEmail,
  userFirstName,
  userLastName,
  existingAccountDetails,
}: InvoicePaymentFormProps) {
  const theme = useTheme()
  const { getAccessTokenSilently } = useAuth0()

  const [formData, setFormData] = useState<BillingInfo>({
    billingLegalName:
      existingAccountDetails?.billing_legal_name || orgName || '',
    billingEmail: existingAccountDetails?.billing_email || userEmail || '',
    billingContactName:
      existingAccountDetails?.billing_contact_name ||
      (userFirstName && userLastName ? `${userFirstName} ${userLastName}` : ''),
    billingContactPhone: existingAccountDetails?.billing_contact_phone || '',
    billingAddressLine1: existingAccountDetails?.billing_address_line1 || '',
    billingAddressLine2: existingAccountDetails?.billing_address_line2 || '',
    billingCity: existingAccountDetails?.billing_city || '',
    billingState: existingAccountDetails?.billing_state || '',
    billingPostalCode: existingAccountDetails?.billing_postal_code || '',
    billingCountry: existingAccountDetails?.billing_country || 'New Zealand',
    taxRegistrationNumber:
      existingAccountDetails?.tax_registration_number || '',
    discountCode: '',
  })

  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const billingFrequency: 'monthly' | 'quarterly' | 'annual' =
    billingFrequencyProp ?? 'annual'

  // Discount validation result lives here because the parent uses it for the
  // price summary. The actual code text and debounced lookup live inside
  // <DiscountCodeInput />.
  const [discountValidation, setDiscountValidation] =
    useState<DiscountCodeValidation | null>(null)

  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState<
    {
      a?: string
      full?: string
      address?: string
    }[]
  >([])
  const [addressLoading, setAddressLoading] = useState(false)
  const [addressSearchText, setAddressSearchText] = useState('')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const apiUrl = import.meta.env.VITE_API_URL

  // Update form state when existing account details are loaded
  useEffect(() => {
    if (existingAccountDetails) {
      setFormData({
        billingLegalName:
          existingAccountDetails.billing_legal_name || orgName || '',
        billingEmail: existingAccountDetails.billing_email || userEmail || '',
        billingContactName:
          existingAccountDetails.billing_contact_name ||
          (userFirstName && userLastName
            ? `${userFirstName} ${userLastName}`
            : ''),
        billingContactPhone: existingAccountDetails.billing_contact_phone || '',
        billingAddressLine1: existingAccountDetails.billing_address_line1 || '',
        billingAddressLine2: existingAccountDetails.billing_address_line2 || '',
        billingCity: existingAccountDetails.billing_city || '',
        billingState: existingAccountDetails.billing_state || '',
        billingPostalCode: existingAccountDetails.billing_postal_code || '',
        billingCountry: existingAccountDetails.billing_country || 'New Zealand',
        taxRegistrationNumber:
          existingAccountDetails.tax_registration_number || '',
        discountCode: '',
      })
    }
  }, [existingAccountDetails, orgName, userEmail, userFirstName, userLastName])

  // Default to tier 4 if no tier is set or tier is invalid
  const pricing =
    TIER_PRICING[tier as keyof typeof TIER_PRICING] || TIER_PRICING[4]
  const monthlyPrice = pricing.monthly

  // Calculate pricing based on frequency
  const getFrequencyMultiplier = () =>
    billingFrequency === 'monthly'
      ? 1
      : billingFrequency === 'quarterly'
        ? 3
        : 12

  const frequencyMultiplier = getFrequencyMultiplier()
  const originalPeriodPrice = getBillingAmount(monthlyPrice, billingFrequency)

  // Apply discount if valid (nullish-coalesce so a 100% discount → final_price 0 stays 0)
  const periodPrice = discountValidation?.valid
    ? discountValidation.final_price ?? originalPeriodPrice
    : originalPeriodPrice

  const gstAmount = calculateGst(periodPrice)
  const totalPrice = periodPrice + gstAmount

  const getFrequencyLabel = () => {
    switch (billingFrequency) {
      case 'monthly':
        return 'month'
      case 'quarterly':
        return 'quarter'
      case 'annual':
        return 'year'
      default:
        return 'year'
    }
  }

  const handleDiscountValidationChange = useCallback(
    (validation: DiscountCodeValidation | null) => {
      setDiscountValidation(validation)
      setFormData((prev) => ({
        ...prev,
        discountCode: validation?.valid ? validation.code || '' : '',
      }))
    },
    [],
  )

  // Address autocomplete functions
  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query || query.length < 3 || !apiUrl) return

      setAddressLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(
          `${apiUrl}/proxy/address/search?s=${encodeURIComponent(query)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )

        if (response.ok) {
          const data = await response.json()
          setAddressOptions(data.addresses || [])
        } else {
          setAddressOptions([])
        }
      } catch (error) {
        setAddressOptions([])
      } finally {
        setAddressLoading(false)
      }
    },
    [apiUrl, getAccessTokenSilently],
  )

  const debouncedAddressSearch = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        searchAddresses(query)
      }, 300)
    },
    [searchAddresses],
  )

  const handleAddressSelect = (
    selectedAddress: {
      a?: string
      full?: string
      address?: string
    } | null,
  ) => {
    if (!selectedAddress) return

    // Parse the address from search results directly to avoid extra API call
    const fullAddress = selectedAddress.a || ''

    // Pattern: "Street Address, Suburb, City PostCode" -> extract components
    const addressParts = fullAddress
      .split(',')
      .map((part: string) => part.trim())

    let streetAddress = ''
    let city = ''
    let postcode = ''

    if (addressParts.length >= 1) {
      streetAddress = addressParts[0]
    }

    if (addressParts.length >= 3) {
      // Last part usually contains "City PostCode"
      const lastPart = addressParts[addressParts.length - 1]
      const postcodeMatch = lastPart.match(/\b(\d{4})\b/)

      if (postcodeMatch) {
        postcode = postcodeMatch[1]
        city = lastPart.replace(/\s*\d{4}\s*$/, '').trim()
      } else {
        city = lastPart
      }
    }

    // Update form fields immediately without API call
    setFormData((prev) => ({
      ...prev,
      billingAddressLine1: streetAddress,
      billingAddressLine2: '', // Not available in search results
      billingCity: city,
      billingState: '', // Not reliably available in search results
      billingPostalCode: postcode,
      billingCountry: 'New Zealand',
    }))

    // Clear the address search dropdown
    setAddressOptions([])

    // Clear any validation errors for address fields
    setValidationErrors((prev) => ({
      ...prev,
      billingAddressLine1: '',
      billingCity: '',
      billingPostalCode: '',
    }))
  }

  const handleInputChange = (field: keyof BillingInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.billingLegalName.trim()) {
      errors.billingLegalName = 'Legal name is required'
    }

    if (!formData.billingEmail.trim()) {
      errors.billingEmail = 'Billing email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billingEmail)) {
      errors.billingEmail = 'Please enter a valid email address'
    }

    if (!formData.billingContactName.trim()) {
      errors.billingContactName = 'Contact name is required'
    }

    if (!formData.billingAddressLine1.trim()) {
      errors.billingAddressLine1 = 'Address is required'
    }

    if (!formData.billingCity.trim()) {
      errors.billingCity = 'City is required'
    }

    if (!formData.billingPostalCode.trim()) {
      errors.billingPostalCode = 'Postal code is required'
    }

    if (!agreedToTerms) {
      errors.terms = 'You must agree to the terms and conditions'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Include billing frequency and discount code in the submitted data
    const submissionData = {
      ...formData,
      billingFrequency,
      discountCode: discountValidation?.valid
        ? formData.discountCode
        : undefined,
    }

    await onSubmit(submissionData)
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Billing Details
      </Typography>

      {/* Pricing Summary */}
      <Paper
        sx={{ p: 3, mb: 4, bgcolor: '#f5f9ff', border: '1px solid #e3f2fd' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Subscription Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{pricing.description}</Typography>
          <Typography variant="body1">${monthlyPrice}/month</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {billingFrequency.charAt(0).toUpperCase() +
              billingFrequency.slice(1)}{' '}
            amount (ex-GST)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${originalPeriodPrice}/{getFrequencyLabel()}
          </Typography>
        </Box>
        {billingFrequency === 'annual' && (
          <Typography
            variant="body2"
            sx={{ mb: 1, color: theme.palette.success.main, fontWeight: 600 }}
          >
            Annual plan discount applied: 20% off the standard 12-month rate.
          </Typography>
        )}

        {/* Show discount if applied */}
        {discountValidation?.valid && (
          <>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.success.main }}
              >
                Discount ({discountValidation.code})
                {` - ${discountValidation.discount_value}% off`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.success.main }}
              >
                -${discountValidation.discount_amount}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Discounted amount (ex-GST)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ${periodPrice}/{getFrequencyLabel()}
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            GST (15%)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${gstAmount}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Total (inc. GST)
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: theme.palette.primary.main }}
          >
            ${totalPrice}/{getFrequencyLabel()}
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 4 }}>
          {/* Discount Code Section */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
          >
            <LocalOfferIcon color="primary" />
            Promotional Code
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <DiscountCodeInput
                originalPrice={originalPeriodPrice}
                frequencyMultiplier={frequencyMultiplier as FrequencyMultiplier}
                onValidationChange={handleDiscountValidationChange}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
          >
            <BusinessIcon color="primary" />
            Billing Information
          </Typography>

          <Grid container spacing={3}>
            {/* Organisation Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Legal Organisation Name"
                value={formData.billingLegalName}
                onChange={(e) =>
                  handleInputChange('billingLegalName', e.target.value)
                }
                error={!!validationErrors.billingLegalName}
                helperText={
                  validationErrors.billingLegalName ||
                  'The official registered name of your organisation'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST/Tax Registration Number"
                value={formData.taxRegistrationNumber}
                onChange={(e) =>
                  handleInputChange('taxRegistrationNumber', e.target.value)
                }
                helperText="Optional - for organisations registered for GST"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <EmailIcon color="primary" />
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Billing Email"
                type="email"
                value={formData.billingEmail}
                onChange={(e) =>
                  handleInputChange('billingEmail', e.target.value)
                }
                error={!!validationErrors.billingEmail}
                helperText={
                  validationErrors.billingEmail ||
                  'Invoices will be sent to this email'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.billingContactName}
                onChange={(e) =>
                  handleInputChange('billingContactName', e.target.value)
                }
                error={!!validationErrors.billingContactName}
                helperText={validationErrors.billingContactName}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.billingContactPhone}
                onChange={(e) =>
                  handleInputChange('billingContactPhone', e.target.value)
                }
                helperText="Optional"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <LocationOnIcon color="primary" />
                Billing Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={addressOptions}
                getOptionLabel={(option) =>
                  option.a || option.full || option.address || ''
                }
                loading={addressLoading}
                onInputChange={(event, newInputValue) => {
                  setAddressSearchText(newInputValue)
                  if (newInputValue && newInputValue.length >= 3) {
                    debouncedAddressSearch(newInputValue)
                  } else {
                    setAddressOptions([])
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleAddressSelect(newValue)
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for your address"
                    placeholder="Start typing your address..."
                    fullWidth
                    error={!!validationErrors.billingAddressLine1}
                    helperText={
                      validationErrors.billingAddressLine1 ||
                      'Start typing to search for your New Zealand address'
                    }
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  addressSearchText.length < 3
                    ? 'Type at least 3 characters'
                    : 'No addresses found'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.billingAddressLine1}
                onChange={(e) =>
                  handleInputChange('billingAddressLine1', e.target.value)
                }
                error={!!validationErrors.billingAddressLine1}
                helperText={
                  validationErrors.billingAddressLine1 ||
                  'Auto-filled from address search above'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.billingAddressLine2}
                onChange={(e) =>
                  handleInputChange('billingAddressLine2', e.target.value)
                }
                helperText="Optional - Suite, floor, building, etc. Auto-filled from address search"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.billingCity}
                onChange={(e) =>
                  handleInputChange('billingCity', e.target.value)
                }
                error={!!validationErrors.billingCity}
                helperText={
                  validationErrors.billingCity ||
                  'Auto-filled from address search'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State/Region"
                value={formData.billingState}
                onChange={(e) =>
                  handleInputChange('billingState', e.target.value)
                }
                helperText="Auto-filled from address search"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.billingPostalCode}
                onChange={(e) =>
                  handleInputChange('billingPostalCode', e.target.value)
                }
                error={!!validationErrors.billingPostalCode}
                helperText={
                  validationErrors.billingPostalCode ||
                  'Auto-filled from address search'
                }
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.billingCountry}
                onChange={(e) =>
                  handleInputChange('billingCountry', e.target.value)
                }
                helperText="Auto-filled from address search"
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />
            </Grid>

            {/* Terms Agreement */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <a
                      href="https://www.fundsorter.com/termsofservice"
                      target="_blank"
                      style={{ color: theme.palette.primary.main }}
                    >
                      Terms and Conditions
                    </a>{' '}
                    and authorise invoicing for this subscription
                  </Typography>
                }
              />
              {validationErrors.terms && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  {validationErrors.terms}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Form Actions */}
          <Box
            sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}
          >
            {onBack && (
              <Button
                variant="text"
                disableRipple
                onClick={onBack}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
            )}
            <Button
              disableRipple
              type="submit"
              variant="contained"
              disabled={isLoading}
              size="large"
              sx={{ minWidth: 200, textTransform: 'none' }}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
            >
              {isLoading ? 'Setting Up...' : 'Set Up Invoice Billing'}
            </Button>
          </Box>
        </Paper>
      </form>
    </Box>
  )
}
